import type { ReactNode } from "react";
import { cn } from "./cn";

export interface DataTableColumn<T> {
  id: string;
  header: string;
  cell: (item: T) => ReactNode;
  className?: string;
  headerClassName?: string;
  hideLabelOnMobile?: boolean;
}

export interface DataTableProps<T> {
  caption: string;
  columns: DataTableColumn<T>[];
  items: T[];
  getRowKey: (item: T) => string | number;
  mobileTitle?: (item: T) => ReactNode;
  className?: string;
}

export function DataTable<T>({
  caption,
  columns,
  items,
  getRowKey,
  mobileTitle,
  className
}: DataTableProps<T>) {
  return (
    <div className={className}>
      <div className="hidden overflow-x-auto md:block">
        <table className="w-full border-collapse text-left text-sm">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-zinc-200 bg-muted/50 dark:border-zinc-800">
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  className={cn(
                    "whitespace-nowrap px-4 py-3 text-xs font-medium text-zinc-500 dark:text-zinc-400",
                    column.headerClassName
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-200 dark:divide-zinc-800">
            {items.map((item) => (
              <tr
                key={getRowKey(item)}
                className="bg-card transition-colors hover:bg-muted/50"
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn("px-4 py-3 align-middle text-zinc-700 dark:text-zinc-200", column.className)}
                  >
                    {column.cell(item)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ul className="divide-y divide-zinc-200 md:hidden dark:divide-zinc-800" aria-label={caption}>
        {items.map((item) => (
          <li key={getRowKey(item)} className="space-y-3 bg-card p-4">
            {mobileTitle ? (
              <div className="font-semibold text-zinc-950 dark:text-white">{mobileTitle(item)}</div>
            ) : null}
            <dl className="space-y-2">
              {columns.map((column) => (
                <div key={column.id} className="flex items-start justify-between gap-4 text-sm">
                  {column.hideLabelOnMobile ? null : (
                    <dt className="shrink-0 text-zinc-500 dark:text-zinc-400">{column.header}</dt>
                  )}
                  <dd
                    className={cn(
                      "min-w-0 text-right text-zinc-700 dark:text-zinc-200",
                      column.hideLabelOnMobile && "ml-auto",
                      column.className
                    )}
                  >
                    {column.cell(item)}
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
    </div>
  );
}
